
import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Wrench, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ServicesManagementCard from '@/components/doctor/ServicesManagementCard';
import ServiceVisibilityCard from '@/components/doctor/ServiceVisibilityCard';
import AddServiceModal from '@/components/doctor/AddServiceModal';
import RequestServiceModal from '@/components/doctor/RequestServiceModal';
import ServiceRequestHistory from '@/components/doctor/ServiceRequestHistory';
import { availableTreatments, Treatment } from '@/data/treatmentData';

const DoctorServicesPage = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("dental");
  
  // State for service modals
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [requestServiceModalOpen, setRequestServiceModalOpen] = useState(false);
  
  // Initialize state with treatment data
  const [dentalTreatments, setDentalTreatments] = useState<Treatment[]>(
    availableTreatments.dental.map(t => ({ ...t, enabled: false, category: t.category as 'dental' | 'cosmetic' }))
  );
  const [cosmeticTreatments, setCosmeticTreatments] = useState<Treatment[]>(
    availableTreatments.cosmetic.map(t => ({ ...t, enabled: false, category: t.category as 'dental' | 'cosmetic' }))
  );
  
  const handleToggleTreatment = (id: string, category: 'dental' | 'cosmetic') => {
    if (category === 'dental') {
      setDentalTreatments(prev => 
        prev.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t)
      );
    } else {
      setCosmeticTreatments(prev => 
        prev.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t)
      );
    }
  };
  
  const handlePriceChange = (id: string, value: string, category: 'dental' | 'cosmetic') => {
    const price = value === '' ? undefined : parseFloat(value);
    
    if (category === 'dental') {
      setDentalTreatments(prev => 
        prev.map(t => t.id === id ? { ...t, customPrice: price } : t)
      );
    } else {
      setCosmeticTreatments(prev => 
        prev.map(t => t.id === id ? { ...t, customPrice: price } : t)
      );
    }
  };
  
  const handleSaveChanges = () => {
    // In a real app, this would call an API to save the changes
    toast({
      title: "Services updated",
      description: "Your services and pricing have been successfully updated.",
    });
  };
  
  const handleAddServices = (selectedServices: { dental: Treatment[]; cosmetic: Treatment[] }) => {
    // Update the dental treatments
    setDentalTreatments(prevTreatments => {
      const newTreatments = [...prevTreatments];
      
      // Add or update treatments based on selection
      selectedServices.dental.forEach(selectedTreatment => {
        const existingIndex = newTreatments.findIndex(t => t.id === selectedTreatment.id);
        
        if (existingIndex >= 0) {
          newTreatments[existingIndex] = selectedTreatment;
        } else {
          newTreatments.push(selectedTreatment);
        }
      });
      
      return newTreatments;
    });
    
    // Update the cosmetic treatments
    setCosmeticTreatments(prevTreatments => {
      const newTreatments = [...prevTreatments];
      
      // Add or update treatments based on selection
      selectedServices.cosmetic.forEach(selectedTreatment => {
        const existingIndex = newTreatments.findIndex(t => t.id === selectedTreatment.id);
        
        if (existingIndex >= 0) {
          newTreatments[existingIndex] = selectedTreatment;
        } else {
          newTreatments.push(selectedTreatment);
        }
      });
      
      return newTreatments;
    });
    
    toast({
      title: "Services added",
      description: "New services have been added to your profile.",
    });
  };

  // Create properly typed availableTreatments for modal
  const typedAvailableTreatments = {
    dental: availableTreatments.dental.map(t => ({
      ...t,
      category: t.category as 'dental' | 'cosmetic'
    })),
    cosmetic: availableTreatments.cosmetic.map(t => ({
      ...t,
      category: t.category as 'dental' | 'cosmetic'
    }))
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto p-4 pb-24 pt-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2 mb-4 md:mb-0">
            <Wrench className="h-6 w-6 text-primary" />
            My Services & Pricing
          </h1>
          
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <Button 
              onClick={() => setAddServiceModalOpen(true)}
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => setRequestServiceModalOpen(true)}
              className="flex items-center"
            >
              <Search className="h-4 w-4 mr-2" />
              Request a New Service
            </Button>
          </div>
        </div>
        
        <ServicesManagementCard
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          dentalTreatments={dentalTreatments}
          cosmeticTreatments={cosmeticTreatments}
          handleToggleTreatment={handleToggleTreatment}
          handlePriceChange={handlePriceChange}
          handleSaveChanges={handleSaveChanges}
        />
        
        <ServiceVisibilityCard
          dentalTreatments={dentalTreatments}
          cosmeticTreatments={cosmeticTreatments}
        />
        
        <ServiceRequestHistory />
      </div>
      
      <AddServiceModal
        open={addServiceModalOpen}
        onOpenChange={setAddServiceModalOpen}
        availableTreatments={typedAvailableTreatments}
        selectedTreatments={{
          dental: dentalTreatments.filter(t => t.enabled),
          cosmetic: cosmeticTreatments.filter(t => t.enabled)
        }}
        onSave={handleAddServices}
      />
      
      <RequestServiceModal
        open={requestServiceModalOpen}
        onOpenChange={setRequestServiceModalOpen}
      />
    </div>
  );
};

export default DoctorServicesPage;
